/**
 * @fileoverview MissingTimesheetsReport — who has not entered their time
 * (MOR-1460).
 *
 * Jess's "one button" before she prepares reports: every person in the firm
 * against their work schedule for a period, worst first, with the days each
 * one is short. Reads `missingTimesheets.report`, which admits firm-wide
 * approvers and organisation admins. Exports to Excel like the pivot tables.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import {
  addDays,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  subMonths,
} from "date-fns";
import { Download } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { cn } from "@/utils/misc";
import { Button, Label } from "@/ui/primitives";
import { Spinner } from "@/ui/primitives/spinner";
import { Switch } from "@/ui/primitives/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { formatDateRange, formatUnitsAsHours } from "./format";
import {
  buildMissingTimesheetsSheet,
  exportMissingTimesheets,
} from "./missingTimesheetsExport";

const CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-primary/10";

type Preset = "thisMonth" | "lastMonth" | "last30" | "custom";

const PRESET_LABELS: Record<Exclude<Preset, "custom">, string> = {
  thisMonth: "This month",
  lastMonth: "Last month",
  last30: "Last 30 days",
};

function presetRange(preset: Exclude<Preset, "custom">, today: Date) {
  const iso = (d: Date) => format(d, "yyyy-MM-dd");
  switch (preset) {
    case "thisMonth":
      return { start: iso(startOfMonth(today)), end: iso(endOfMonth(today)) };
    case "lastMonth": {
      const last = subMonths(today, 1);
      return { start: iso(startOfMonth(last)), end: iso(endOfMonth(last)) };
    }
    case "last30":
      return { start: iso(addDays(today, -30)), end: iso(today) };
  }
}

export function MissingTimesheetsReport({
  orgId,
  today = new Date(),
}: {
  orgId: Id<"organizations">;
  /** Injectable for tests; the chase stops at the day before it. */
  today?: Date;
}) {
  const todayIso = format(today, "yyyy-MM-dd");
  const [preset, setPreset] = useState<Preset>("thisMonth");
  const [custom, setCustom] = useState(() => presetRange("thisMonth", today));
  const [onlyShort, setOnlyShort] = useState(true);
  const range = preset === "custom" ? custom : presetRange(preset, today);
  const rangeValid = !!range.start && !!range.end && range.end >= range.start;

  const {
    data: report,
    isError,
    error,
  } = useQuery({
    ...convexQuery(api.timesheets.missingTimesheets.report, {
      orgId,
      startDate: range.start,
      endDate: range.end,
      today: todayIso,
    }),
    enabled: rangeValid,
  });

  const people = (report?.people ?? []).filter(
    (p) => !onlyShort || p.shortUnits > 0,
  );
  const unitMinutes = report?.unitMinutes ?? 6;
  const hours = (units: number) => formatUnitsAsHours(units, unitMinutes);
  const totalShort = people.reduce((sum, p) => sum + p.shortUnits, 0);

  const exportExcel = () => {
    if (!report) return;
    exportMissingTimesheets(
      buildMissingTimesheetsSheet(people, range, report.chasedTo, unitMinutes),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className={cn(CARD_CLASS, "flex flex-wrap items-end gap-4 p-4")}>
        <div className="space-y-1.5">
          <Label>Period</Label>
          <div className="flex items-center rounded-md border border-border">
            {(
              ["thisMonth", "lastMonth", "last30"] as Exclude<
                Preset,
                "custom"
              >[]
            ).map((key, index, all) => (
              <Button
                key={key}
                variant={preset === key ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "h-8 rounded-none",
                  index === 0 && "rounded-l-md",
                  index === all.length - 1 && "rounded-r-md",
                )}
                onClick={() => setPreset(key)}
              >
                {PRESET_LABELS[key]}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>From</Label>
          <DatePicker
            value={range.start}
            onChange={(isoDate) => {
              setPreset("custom");
              setCustom({ ...range, start: isoDate ?? "" });
            }}
            placeholder="From"
          />
        </div>
        <div className="space-y-1.5">
          <Label>To</Label>
          <DatePicker
            value={range.end}
            onChange={(isoDate) => {
              setPreset("custom");
              setCustom({ ...range, end: isoDate ?? "" });
            }}
            placeholder="To"
          />
        </div>
        <label className="flex h-10 items-center gap-2 text-sm">
          <Switch checked={onlyShort} onCheckedChange={setOnlyShort} />
          Only people who are short
        </label>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto h-8"
          disabled={!report || people.length === 0}
          onClick={exportExcel}
        >
          <Download className="size-4" />
          Export Excel
        </Button>
      </div>

      <div className={cn(CARD_CLASS, "flex min-w-0 flex-col gap-3 p-4")}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-semibold">Missing timesheets</p>
          {report && (
            <p className="text-xs text-muted-foreground">
              {formatDateRange(range.start, range.end)}, chased to{" "}
              {format(parseISO(report.chasedTo), "EEE d MMM")}. Today is never
              chased.{" "}
              {people.length === 1 ? "1 person" : `${people.length} people`}
              {onlyShort ? " short" : ""}, {hours(totalShort)} in all.
            </p>
          )}
        </div>
        {!rangeValid ? (
          <p className="py-6 text-sm text-muted-foreground">
            Choose a period that ends on or after it starts.
          </p>
        ) : isError ? (
          <p className="py-6 text-sm text-muted-foreground">
            {error instanceof Error
              ? error.message
              : "Could not run the report."}
          </p>
        ) : !report ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner className="text-muted-foreground" />
          </div>
        ) : people.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">
            {onlyShort
              ? "Nobody is short for this period."
              : "Nobody was expected to record time in this period."}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead className="text-right">Expected</TableHead>
                <TableHead className="text-right">Recorded</TableHead>
                <TableHead className="text-right">Short</TableHead>
                <TableHead>Days short</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {people.map((p) => (
                <TableRow key={p.userId}>
                  <TableCell className="align-top">
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.hasSchedule ? "Own schedule" : "Firm default"}
                    </div>
                  </TableCell>
                  <TableCell className="text-right align-top tabular-nums">
                    {hours(p.expectedUnits)}
                  </TableCell>
                  <TableCell className="text-right align-top tabular-nums">
                    {hours(p.recordedUnits)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right align-top font-medium tabular-nums",
                      p.shortUnits > 0 && "text-amber-700 dark:text-amber-400",
                    )}
                  >
                    {hours(p.shortUnits)}
                  </TableCell>
                  <TableCell className="align-top">
                    <DaysShortCell days={p.missing} hours={hours} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

/** How many short days a row shows before folding the rest behind a button. */
const DAYS_SHOWN = 6;

function DaysShortCell({
  days,
  hours,
}: {
  days: {
    date: string;
    expectedUnits: number;
    recordedUnits: number;
    shortUnits: number;
  }[];
  hours: (units: number) => string;
}) {
  const [showAll, setShowAll] = useState(false);
  if (days.length === 0) {
    return <span className="text-xs text-muted-foreground">Up to date</span>;
  }
  const shown = showAll ? days : days.slice(0, DAYS_SHOWN);
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((d) => (
        <span
          key={d.date}
          className="rounded bg-amber-50 px-1.5 py-0.5 text-xs tabular-nums text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          title={`Expected ${hours(d.expectedUnits)}, recorded ${hours(d.recordedUnits)}`}
        >
          {format(parseISO(d.date), "EEE d MMM")}
          {d.recordedUnits > 0 ? ` (${hours(d.shortUnits)} short)` : ""}
        </span>
      ))}
      {days.length > DAYS_SHOWN && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs text-muted-foreground"
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll ? "Show fewer" : `Show all ${days.length}`}
        </Button>
      )}
    </div>
  );
}
